// Army stacks along sourced front lines: where they stand and what they are made of. Pure planning,
// no GPU; units.ts draws the result.
//
//   front line ─simplify─▶ sites at 1/2, 1/4, 3/4, 1/8 … of it ─▶ candidate stacks, on the line
//   candidates + camera ─▶ selectStacks: the central ones first, none within SCREEN_SPACING px of
//                          another, at most PER_FRONT per front and PER_SCREEN on screen
//
//   stack frame (yaw = facing):          forward
//                                           ▲                 holder │ line │ foe: two clusters face
//                                     tank  ●                 each other across the line; the foe's
//                                  inf ●    │    ● inf        cluster is drawn only where the ground
//                                           ● gun             on the far side belongs to another state
//
// Truth: a stack exists only on a sourced, current front line and carries that front's sources. Every
// stack is the same four models per side: a symbol of "forces on this line", never a count or a
// strength. How many are drawn depends on the screen, not on how convoluted the line is.

import { hexAtWorld } from '../../core/grid.ts';
import type { SourceId } from '../../core/schema.ts';
import { dyadicAnchors, simplify, snapToLine } from './frontGeo.ts';
import type { FrontRecord } from './fronts.ts';

/** Closest two candidate stacks stand on one line (world units). */
const MIN_GAP = 1.6;
/** Lines shorter than this get no stack. */
export const MIN_LINE = 1.2;
/** Bends of a line smaller than this (world units, a bit over half a hex) do not move its stacks. */
const SIMPLIFY_TOLERANCE = 1;
/** How far to each side of the line the facing test looks for the other side's ground. */
const PROBE = 0.9;
/** How far each side's cluster stands back from the line when both sides are drawn (model units). */
const SIDE_OFFSET = 0.42;

// Screen budget: stacks are markers, not a census.
export const SCREEN_SPACING = 110; // px between any two stacks
export const PER_FRONT = 8;
export const PER_SCREEN = 24;
const INFANTRY_TURN = 0.22; // radians: flank infantry are not perfectly aligned

export type UnitKind = 'tank' | 'artillery' | 'infantry';

/** Where a model stands in the stack frame (right, forward), in world units at scale 1. */
interface Member {
  kind: UnitKind;
  right: number;
  forward: number;
  turn: number;
}

export const MEMBERS: readonly Member[] = [
  { kind: 'tank', right: 0, forward: 0.22, turn: 0 },
  { kind: 'artillery', right: 0, forward: -0.28, turn: 0 },
  { kind: 'infantry', right: -0.34, forward: 0, turn: -INFANTRY_TURN },
  { kind: 'infantry', right: 0.34, forward: 0, turn: INFANTRY_TURN },
];

/** A stack on a front line: a hover target with its sources. */
export interface UnitStack {
  /** Stable id for tooltips: front id, line index, slot along the line. */
  id: string;
  /** Front series id (snapshot.fronts[].id). */
  front: string;
  /** Actor id holding the front (snapshot.fronts[].controller). */
  controller: string;
  /** Actor index whose colour the stack wears (0 = none known). */
  actor: number;
  /** Actor index of the state on the far side of the line (0 = none known: no opposing cluster). */
  foe: number;
  /** World units of line this stack stands for: central stacks (big) are chosen first. */
  gap: number;
  asOf: string;
  sources: SourceId[];
  /** Hex under the anchor. */
  hex: number;
  x: number;
  z: number;
  /** Facing, radians about +y (0 = +z). */
  yaw: number;
}

/** A model of a stack placed in the world. */
export interface Placed {
  kind: UnitKind;
  x: number;
  z: number;
  yaw: number;
  /** Actor index whose colour the model wears. */
  actor: number;
}

/** Facing of a stack at (x, z) on a line with unit direction (tx, tz). */
export type Facing = (x: number, z: number, tx: number, tz: number) => number;

/** Face along the line. */
export const alongLine: Facing = (_x, _z, tx, tz) => Math.atan2(tx, tz);

/** Actor holding the ground at (x, z): whoever controls the hex, else its owner (0 = nobody). */
function holderAt(owner: Uint16Array, controller: Uint16Array, x: number, z: number): number {
  const hex = hexAtWorld(x, z);
  return controller[hex] || owner[hex];
}

/** Face the side of the line that `actor` does not hold; along the line when both sides look alike. */
export function towardEnemy(owner: Uint16Array, controller: Uint16Array, actor: number): Facing {
  const held = (x: number, z: number): boolean => holderAt(owner, controller, x, z) === actor;

  return (x, z, tx, tz) => {
    const nx = -tz;
    const nz = tx;
    const left = held(x + nx * PROBE, z + nz * PROBE);
    const right = held(x - nx * PROBE, z - nz * PROBE);
    if (left === right || actor === 0) {
      return alongLine(x, z, tx, tz);
    }

    const side = left ? -1 : 1;
    return Math.atan2(nx * side, nz * side);
  };
}

/** Actor index of the state across a line from the point (0 = unknown). */
export type FoeOf = (x: number, z: number, tx: number, tz: number) => number;

export const noFoe: FoeOf = () => 0;

/** The state holding the ground on the side of the line that `actor` does not hold; 0 when a side is nobody's. */
export function foeOn(owner: Uint16Array, controller: Uint16Array, actor: number): FoeOf {
  return (x, z, tx, tz) => {
    if (actor === 0) {
      return 0;
    }

    const left = holderAt(owner, controller, x - tz * PROBE, z + tx * PROBE);
    const right = holderAt(owner, controller, x + tz * PROBE, z - tx * PROBE);
    if (left === actor && right !== actor) {
      return right;
    }

    return right === actor && left !== actor ? left : 0;
  };
}

function byGap(a: UnitStack, b: UnitStack): number {
  return b.gap - a.gap || (a.id < b.id ? -1 : 1);
}

/**
 * Candidate stacks of a front, central first. A stale front gets none: the line is old news. The
 * simplified line sets where they fall, so a wiggle does not change them; each stands on the true line.
 */
export function planStacks(front: FrontRecord, facing: Facing, foeOf: FoeOf = noFoe): UnitStack[] {
  if (front.stale) {
    return [];
  }

  const out: UnitStack[] = [];

  front.lines.forEach((line, lineIndex) => {
    for (const site of dyadicAnchors(simplify(line, SIMPLIFY_TOLERANCE), MIN_GAP, MIN_LINE)) {
      const at = snapToLine(line, site.x, site.z);
      out.push({
        id: `${front.id}:${lineIndex}:${site.slot}`,
        front: front.id,
        controller: front.controller,
        actor: front.actor,
        foe: foeOf(at.x, at.z, site.tx, site.tz),
        gap: site.gap,
        asOf: front.asOf,
        sources: front.sources,
        hex: hexAtWorld(at.x, at.z),
        x: at.x,
        z: at.z,
        yaw: facing(at.x, at.z, site.tx, site.tz),
      });
    }
  });

  return out.sort(byGap);
}

/** Candidate stacks of every front, central first across all of them. */
export function planAll(fronts: readonly FrontRecord[], owner: Uint16Array, controller: Uint16Array): UnitStack[] {
  return fronts.flatMap((f) => planStacks(f, towardEnemy(owner, controller, f.actor), foeOn(owner, controller, f.actor))).sort(byGap);
}

/** A candidate stack seen on screen at (px, py) pixels. */
export interface Sighting {
  stack: UnitStack;
  px: number;
  py: number;
}

/**
 * The sightings worth drawing, taking `seen` (candidates on screen, central first) in order: one is
 * dropped when it stands within SCREEN_SPACING px of a kept one, or when its front or the screen is full.
 */
export function selectStacks<T extends Sighting>(seen: readonly T[]): T[] {
  const kept: T[] = [];
  const perFront = new Map<string, number>();

  for (const s of seen) {
    if (kept.length >= PER_SCREEN) {
      break;
    }

    const used = perFront.get(s.stack.front) ?? 0;
    if (used >= PER_FRONT) {
      continue;
    }

    if (kept.some((k) => Math.hypot(k.px - s.px, k.py - s.py) < SCREEN_SPACING)) {
      continue;
    }

    kept.push(s);
    perFront.set(s.stack.front, used + 1);
  }

  return kept;
}

/** One side's four models, `back` model units behind the anchor along the stack's facing. */
function cluster(stack: UnitStack, scale: number, turn: number, back: number, actor: number): Placed[] {
  const yaw = stack.yaw + turn;
  const sin = Math.sin(yaw);
  const cos = Math.cos(yaw);
  const ax = stack.x - Math.sin(stack.yaw) * back * scale;
  const az = stack.z - Math.cos(stack.yaw) * back * scale;

  return MEMBERS.map((m) => ({
    kind: m.kind,
    x: ax + (m.right * cos + m.forward * sin) * scale,
    z: az + (m.forward * cos - m.right * sin) * scale,
    yaw: yaw + m.turn,
    actor,
  }));
}

/**
 * The models of a stack in the world; `scale` grows the spread with the models. The holder faces
 * the line; when a foe is known its cluster stands opposite, facing back.
 */
export function placeStack(stack: UnitStack, scale: number): Placed[] {
  if (stack.foe <= 0) {
    return cluster(stack, scale, 0, 0, stack.actor);
  }

  return [...cluster(stack, scale, 0, SIDE_OFFSET, stack.actor), ...cluster(stack, scale, Math.PI, -SIDE_OFFSET, stack.foe)];
}
